import { useEffect, useState } from "react";

import Banque from "./Banque";
import Bilans from "./Bilans";
import BilansQcm from "./BilansQcm";
import Connexion from "./Connexion";
import Classes from "./Classes";
import Corbeille from "./Corbeille";
import Competences from "./Competences";
import ComposerQcm from "./ComposerQcm";
import CompetencesQcm from "./CompetencesQcm";
import GrilleQcm from "./GrilleQcm";
import GrilleSaisie from "./GrilleSaisie";
import ImportDs from "./ImportDs";
import ImportQcm from "./ImportQcm";
import ResultatsQcm from "./ResultatsQcm";
import Statistiques from "./Statistiques";
import SujetQcm from "./SujetQcm";
import {
  api,
  SessionExpiree,
  type ImpactDs,
  type ImpactQcm,
  type ResumeDs,
  type ResumeQcm,
  type Utilisateur,
} from "./api";

type Onglet = "devoirs" | "qcm" | "classes";
type Vue = "saisie" | "competences" | "statistiques" | "bilans";
// Un QCM a une vue que les devoirs n'ont pas : le sujet. Celui d'un devoir
// est écrit et compilé hors d'Atelier ; celui d'un QCM, c'est Atelier qui le
// fabrique. Sa vue « Compétences » ne fait pas le même travail que celle d'un
// devoir : les capacités s'y attachent à la question, pas à la compétence, et
// une question déjà rattachée par la banque garde les siennes.
type VueQcm = "sujet" | "saisie" | "resultats" | "competences" | "bilans";

const ONGLETS: Record<Onglet, string> = {
  devoirs: "Devoirs",
  qcm: "QCM",
  classes: "Classes",
};

const VUES: Record<Vue, string> = {
  saisie: "Saisie des notes",
  competences: "Compétences",
  statistiques: "Statistiques",
  bilans: "Bilans",
};

const VUES_QCM: Record<VueQcm, string> = {
  sujet: "Sujet",
  saisie: "Saisie des réponses",
  resultats: "Résultats",
  competences: "Compétences",
  bilans: "Bilans",
};

/** Avancement de la saisie d'un QCM, tel qu'affiché dans la liste. */
function etatSaisie(qcm: ResumeQcm) {
  if (qcm.reponses_attendues === 0) {
    return { libelle: "aucune copie", classe: "attente" };
  }
  if (qcm.reponses_saisies === 0) {
    return { libelle: "non commencée", classe: "attente" };
  }
  if (qcm.reponses_saisies >= qcm.reponses_attendues) {
    return { libelle: "achevée", classe: "achevee" };
  }
  const part = Math.round((qcm.reponses_saisies / qcm.reponses_attendues) * 100);
  return { libelle: `${Math.min(part, 99)} %`, classe: "en-cours" };
}

/** Avancement de la correction d'un devoir, tel qu'affiché dans la liste. */
function etatCorrection(devoir: ResumeDs) {
  if (devoir.notes_attendues === 0) {
    return { libelle: "aucune copie", classe: "attente" };
  }
  if (devoir.notes_saisies === 0) {
    return { libelle: "non commencée", classe: "attente" };
  }
  if (devoir.notes_saisies >= devoir.notes_attendues) {
    return { libelle: "achevée", classe: "achevee" };
  }
  const part = Math.round((devoir.notes_saisies / devoir.notes_attendues) * 100);
  // Un arrondi à 100 % sur une correction inachevée serait un mensonge.
  return { libelle: `${Math.min(part, 99)} %`, classe: "en-cours" };
}

export default function App() {
  const [utilisateur, setUtilisateur] = useState<Utilisateur | null>(null);
  const [verifie, setVerifie] = useState(false);
  const [devoirs, setDevoirs] = useState<ResumeDs[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [choisi, setChoisi] = useState<number | null>(null);
  const [onglet, setOnglet] = useState<Onglet>("devoirs");
  const [vue, setVue] = useState<Vue>("saisie");
  const [qcms, setQcms] = useState<ResumeQcm[] | null>(null);
  const [qcmChoisi, setQcmChoisi] = useState<number | null>(null);
  const [vueQcm, setVueQcm] = useState<VueQcm>("sujet");
  // La composition a un début et une fin : on y entre, on en sort. Pas de
  // panier à gérer entre deux visites — le professeur compose d'une traite.
  const [composition, setComposition] = useState(false);
  const [aSupprimer, setASupprimer] = useState<ImpactDs | null>(null);
  const [qcmASupprimer, setQcmASupprimer] = useState<ImpactQcm | null>(null);
  // Incrémenté à chaque suppression : c'est ce qui fait que la corbeille
  // montre ce qu'on vient d'y jeter, et non son état d'avant.
  const [suppressions, setSuppressions] = useState(0);

  // Une session peut déjà être ouverte : on le vérifie avant d'afficher quoi
  // que ce soit, sinon l'écran de connexion clignoterait à chaque rechargement.
  useEffect(() => {
    api
      .lireSession()
      .then(setUtilisateur)
      .catch(() => setUtilisateur(null))
      .finally(() => setVerifie(true));
  }, []);

  // Rechargée à chaque retour à la liste : les compétences enregistrées et
  // l'avancement de la correction changent pendant qu'on est dans un devoir.
  // La liste des QCM suit la même règle que celle des devoirs : rechargée au
  // retour, parce que la saisie avance pendant qu'on est dedans.
  useEffect(() => {
    if (qcmChoisi !== null || utilisateur === null || onglet !== "qcm") return;
    api.listerQcm().then(setQcms).catch((e: Error) => {
      if (e instanceof SessionExpiree) setUtilisateur(null);
      else setErreur(e.message);
    });
  }, [qcmChoisi, utilisateur, onglet]);

  useEffect(() => {
    if (choisi !== null || utilisateur === null) return;
    api
      .listerDs()
      .then(setDevoirs)
      .catch((e: Error) => {
        // Session expirée pendant la navigation : retour à la connexion.
        if (e instanceof SessionExpiree) setUtilisateur(null);
        else setErreur(e.message);
      });
  }, [choisi, utilisateur]);

  if (!verifie) return <main />;
  if (utilisateur === null) return <Connexion surConnexion={setUtilisateur} />;

  if (qcmChoisi !== null) {
    return (
      <main className="large">
        <div className="barre-devoir">
          <button type="button" className="retour" onClick={() => setQcmChoisi(null)}>
            ← Tous les QCM
          </button>
          <nav className="onglets compacts">
            {(Object.keys(VUES_QCM) as VueQcm[]).map((nom) => (
              <button
                key={nom}
                type="button"
                className={vueQcm === nom ? "actif" : ""}
                onClick={() => setVueQcm(nom)}
              >
                {VUES_QCM[nom]}
              </button>
            ))}
          </nav>
        </div>
        {vueQcm === "sujet" && <SujetQcm qcmId={qcmChoisi} />}
        {vueQcm === "saisie" && <GrilleQcm qcmId={qcmChoisi} />}
        {vueQcm === "resultats" && <ResultatsQcm qcmId={qcmChoisi} />}
        {vueQcm === "competences" && <CompetencesQcm qcmId={qcmChoisi} />}
        {vueQcm === "bilans" && <BilansQcm qcmId={qcmChoisi} />}
      </main>
    );
  }

  if (choisi !== null) {
    return (
      <main className="large">
        <div className="barre-devoir">
          <button type="button" className="retour" onClick={() => setChoisi(null)}>
            ← Tous les devoirs
          </button>
          <nav className="onglets compacts">
            {(Object.keys(VUES) as Vue[]).map((nom) => (
              <button
                key={nom}
                type="button"
                className={vue === nom ? "actif" : ""}
                onClick={() => setVue(nom)}
              >
                {VUES[nom]}
              </button>
            ))}
          </nav>
        </div>
        {vue === "saisie" && <GrilleSaisie dsId={choisi} />}
        {vue === "competences" && <Competences dsId={choisi} />}
        {vue === "statistiques" && <Statistiques dsId={choisi} />}
        {vue === "bilans" && <Bilans dsId={choisi} />}
      </main>
    );
  }

  return (
    <main>
      <header className="entete-compte">
        <div>
          <h1>Atelier</h1>
          <p className="sous-titre">
            Évaluation par compétences : de l'énoncé LaTeX au feedback individuel
          </p>
        </div>
        <div className="compte">
          {utilisateur.nom_affiche}
          <button
            type="button"
            onClick={async () => {
              await api.fermerSession();
              setUtilisateur(null);
              setDevoirs(null);
            }}
          >
            Se déconnecter
          </button>
        </div>
      </header>

      <nav className="onglets">
        {(Object.keys(ONGLETS) as Onglet[]).map((nom) => (
          <button
            key={nom}
            type="button"
            className={onglet === nom ? "actif" : ""}
            onClick={() => setOnglet(nom)}
          >
            {ONGLETS[nom]}
          </button>
        ))}
      </nav>

      {onglet === "classes" && <Classes />}

      {onglet === "qcm" && composition && (
        <ComposerQcm
          surComposition={() => {
            setComposition(false);
            api.listerQcm().then(setQcms);
          }}
          surAbandon={() => setComposition(false)}
        />
      )}

      {onglet === "qcm" && !composition && (
        <section>
          <header className="saisie-entete">
            <h2>QCM</h2>
            <div className="actions">
              <button type="button" onClick={() => setComposition(true)}>
                Composer depuis la banque
              </button>
            </div>
          </header>
          {!qcms && <p>Chargement…</p>}
          {qcms && qcms.length === 0 && (
            <p className="aide">
              Aucun QCM pour l'instant. L'import d'un fichier de questions{" "}
              <code>.typ</code> se fait ci-dessous.
            </p>
          )}
          {qcms && qcms.length > 0 && (
            <table className="liste-ds">
              <thead>
                <tr>
                  <th>QCM</th>
                  <th>Classe</th>
                  <th>Questions</th>
                  <th>Versions</th>
                  <th>Compétences</th>
                  <th>Saisie</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {qcms.map((qcm) => {
                  const avancement = etatSaisie(qcm);
                  return (
                    <tr key={qcm.id}>
                      <td>
                        <button type="button" onClick={() => setQcmChoisi(qcm.id)}>
                          {qcm.titre}
                        </button>
                        <div className="fichier">{qcm.nom_fichier}</div>
                      </td>
                      <td>{qcm.classe}</td>
                      <td className="nombre">{qcm.questions}</td>
                      <td className="nombre">{qcm.versions}</td>
                      <td className="nombre">
                        {qcm.competences || (
                          <span className="attente">à identifier</span>
                        )}
                      </td>
                      <td className="nombre">
                        <span className={avancement.classe}>{avancement.libelle}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="retirer"
                          title="Supprimer ce QCM"
                          onClick={() =>
                            api.impactQcm(qcm.id).then(setQcmASupprimer)
                          }
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {qcmASupprimer && (
            <div className="confirmation">
              <p>
                Supprimer <strong>{qcmASupprimer.titre}</strong> effacera{" "}
                <strong>{qcmASupprimer.questions} questions</strong>,{" "}
                <strong>{qcmASupprimer.copies} copies</strong> et{" "}
                <strong>
                  {qcmASupprimer.reponses_saisies} réponses saisies
                </strong>
                {qcmASupprimer.competences > 0 &&
                  `, ainsi que ${qcmASupprimer.competences} compétences`}
                .
              </p>
              {qcmASupprimer.dernier_sujet_le && (
                <p>
                  Un sujet a été produit le{" "}
                  {new Date(qcmASupprimer.dernier_sujet_le).toLocaleDateString(
                    "fr-FR",
                  )}
                  . <strong>Si ce QCM a été distribué</strong>, ses questions
                  sont sorties de la salle : après suppression, Atelier ne vous
                  avertira plus que la {qcmASupprimer.classe} les a vues.
                </p>
              )}
              <p>
                Une sauvegarde complète sera déposée dans la corbeille : le QCM
                restera restaurable. <strong>Les questions de la banque ne
                sont pas touchées</strong> — elles ne dépendent pas de ce QCM,
                et c'est là qu'une restauration les retrouvera.
              </p>
              <button
                type="button"
                className="danger"
                onClick={async () => {
                  await api.supprimerQcm(qcmASupprimer.qcm_id, true);
                  setQcmASupprimer(null);
                  setSuppressions((n) => n + 1);
                  api.listerQcm().then(setQcms);
                }}
              >
                Confirmer la suppression
              </button>
              <button type="button" onClick={() => setQcmASupprimer(null)}>
                Annuler
              </button>
            </div>
          )}

          <ImportQcm surImport={() => api.listerQcm().then(setQcms)} />

          {/* La corbeille est un répertoire unique, partagé par les deux
              familles : elle doit être atteignable depuis l'onglet où l'on
              vient de supprimer, sinon restaurer un QCM oblige à passer par
              l'onglet Devoirs. */}
          <Corbeille
            signal={suppressions}
            surRestauration={() => api.listerQcm().then(setQcms)}
          />

          {/* La banque va sous la liste des QCM, pas en quatrième onglet : on
              la consulte rarement, et toujours en composant. */}
          <Banque />
        </section>
      )}

      {onglet === "devoirs" && (
      <section>
        <h2>Devoirs</h2>
        {erreur && <p className="erreur">API injoignable : {erreur}</p>}
        {!devoirs && !erreur && <p>Chargement…</p>}
        {devoirs && devoirs.length === 0 && (
          <p className="aide">
            Aucun devoir pour l'instant. L'import d'un énoncé{" "}
            <code>.tex</code> se fait ci-dessous.
          </p>
        )}
        {devoirs && devoirs.length > 0 && (
          <table className="liste-ds">
            <thead>
              <tr>
                <th>Devoir</th>
                <th>Classe</th>
                <th>Questions</th>
                <th>Compétences</th>
                <th>Correction</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devoirs.map((devoir) => {
                const avancement = etatCorrection(devoir);
                return (
                  <tr key={devoir.id}>
                    <td>
                      <button type="button" onClick={() => setChoisi(devoir.id)}>
                        {devoir.titre}
                      </button>
                      <div className="fichier">{devoir.nom_fichier}</div>
                    </td>
                    <td>{devoir.classe}</td>
                    <td className="nombre">{devoir.questions}</td>
                    <td className="nombre">
                      {devoir.competences || <span className="attente">à identifier</span>}
                    </td>
                    <td className="nombre">
                      <span className={avancement.classe}>{avancement.libelle}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="retirer"
                        title="Supprimer ce devoir"
                        onClick={() => api.impactDs(devoir.id).then(setASupprimer)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {aSupprimer && (
          <div className="confirmation">
            <p>
              Supprimer <strong>{aSupprimer.titre}</strong> effacera{" "}
              <strong>{aSupprimer.questions} questions</strong>,{" "}
              <strong>{aSupprimer.copies} copies</strong> et{" "}
              <strong>{aSupprimer.notes_saisies} notes saisies</strong>
              {aSupprimer.competences > 0 &&
                `, ainsi que ${aSupprimer.competences} compétences`}
              .
            </p>
            <p>
              Une sauvegarde complète sera déposée dans la corbeille : le devoir
              restera restaurable.
            </p>
            <button
              type="button"
              className="danger"
              onClick={async () => {
                await api.supprimerDs(aSupprimer.ds_id, true);
                setASupprimer(null);
                setSuppressions((n) => n + 1);
                api.listerDs().then(setDevoirs);
              }}
            >
              Confirmer la suppression
            </button>
            <button type="button" onClick={() => setASupprimer(null)}>
              Annuler
            </button>
          </div>
        )}

        <Corbeille
          signal={suppressions}
          surRestauration={() => api.listerDs().then(setDevoirs)}
        />
        <ImportDs surImport={() => api.listerDs().then(setDevoirs)} />
      </section>
      )}
    </main>
  );
}
